import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/src/renderer/components/ui/Button'
import { Link } from '@/src/renderer/components/ui/Link'
import { SetupOfflineMacIcon } from '@/src/renderer/components/icons/SetupOfflineMacIcon'
import { SetupOfflineWinIcon } from '@/src/renderer/components/icons/SetupOfflineWinIcon'
import type { SkipConfirmationDialogProps } from './SkipConfirmationDialog.types'
import './SkipConfirmationDialog.scss'

/**
 * Skip Confirmation Dialog
 * Dialog zur Bestätigung des Offline-Modus beim Überspringen der Anmeldung
 */
export const SkipConfirmationDialog = ({
  isOpen,
  onClose,
  onConfirm,
  platform
}: SkipConfirmationDialogProps) => {
  const { t } = useTranslation()
  const [isClosing, setIsClosing] = useState(false)
  const [shouldRender, setShouldRender] = useState(isOpen)

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true)
      setIsClosing(false)
    } else if (shouldRender) {
      setIsClosing(true)
      const timer = setTimeout(() => {
        setShouldRender(false)
        setIsClosing(false)
      }, 200) // Match animation duration
      return () => clearTimeout(timer)
    }
  }, [isOpen, shouldRender])

  // Keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        handleClose()
      } else if (e.key === 'Enter') {
        e.preventDefault()
        handleConfirm()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  // Handle close with animation
  const handleClose = () => {
    setIsClosing(true)
    setTimeout(() => {
      onClose()
    }, 200) // Wait for animation to complete
  }

  // Handle confirm with animation
  const handleConfirm = () => {
    setIsClosing(true)
    setTimeout(() => {
      onConfirm()
    }, 200) // Wait for animation to complete
  }

  if (!shouldRender) return null

  // OS-spezifische Icon-Komponente auswählen
  const isMac = platform === 'darwin'
  const SetupIcon = isMac ? SetupOfflineMacIcon : SetupOfflineWinIcon

  return (
    <>
      {/* Dark Overlay */}
      <div
        className={`skip-confirmation-overlay ${isClosing ? 'skip-confirmation-overlay--closing' : ''}`}
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        className={`skip-confirmation-dialog ${isClosing ? 'skip-confirmation-dialog--closing' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="skip-dialog-title"
      >
        <div className="skip-confirmation-dialog__wrapper">

          {/* Content */}
          <div className="skip-confirmation-dialog__content">
            <div className="skip-confirmation-dialog__content-inner">

              {/* Icon - OS-spezifische Grafik */}
              <div className="skip-confirmation-dialog__icon">
                <SetupIcon className="skip-confirmation-dialog__icon-image" />
              </div>

              {/* Title */}
              <div className="skip-confirmation-dialog__title-container">
                <h2
                  id="skip-dialog-title"
                  className="skip-confirmation-dialog__title"
                >
                  {t('setup.skipConfirmation.title')}
                </h2>
              </div>

              {/* Description Container */}
              <div className="skip-confirmation-dialog__description-container">
                <p className="skip-confirmation-dialog__description">
                  {t(isMac ? 'setup.skipConfirmation.description1Mac' : 'setup.skipConfirmation.description1Pc')}
                  <br />
                  {t('setup.skipConfirmation.description2')}
                </p>

                {/* Terms Section */}
                <div className="skip-confirmation-dialog__terms">
                  <div className="skip-confirmation-dialog__terms-inner">
                    <div className="skip-confirmation-dialog__terms-content">
                      <p className="skip-confirmation-dialog__terms-text">
                        <span>{t('setup.skipConfirmation.termsPrefix')} </span>
                        <Link href="https://happytask.app/">
                          {t('setup.skipConfirmation.termsLink')}
                        </Link>
                        <span>{t('setup.skipConfirmation.termsMiddle')} </span>
                        <Link href="https://happytask.app/">
                          {t('setup.skipConfirmation.privacyLink')}
                        </Link>
                        <span>{t('setup.skipConfirmation.termsSuffix')}</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Action Bar */}
          <div className="skip-confirmation-dialog__action-bar">
            <Button
              variant="secondary"
              onClick={handleClose}
              shortcut={{ variant: 'character', text: 'ESC' }}
            >
              {t('setup.skipConfirmation.cancel')}
            </Button>
            <Button
              onClick={handleConfirm}
              shortcut={{ variant: 'return' }}
            >
              {t('setup.skipConfirmation.confirm')}
            </Button>
          </div>

        </div>
      </div>
    </>
  )
}
